import { useState, useEffect } from 'react';
import { ChevronDown, Keyboard } from 'lucide-react';
import { usePlatform } from '../../hooks/usePlatform';

/**
 * Global keyboard dismiss button for mobile devices.
 * Shows a floating button when the keyboard is open to easily dismiss it.
 */
export function KeyboardDismissButton() {
  const { isNative, isIOS, isAndroid } = usePlatform();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [activeElement, setActiveElement] = useState<Element | null>(null);

  useEffect(() => {
    // Only enable on native platforms
    if (!isNative) return;

    // Track focus on input elements
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const isInput = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.tagName === 'SELECT' ||
        target.isContentEditable;
      
      if (isInput) {
        setIsKeyboardOpen(true);
        setActiveElement(target);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      // Small delay to check if focus moved to another input
      setTimeout(() => {
        const active = document.activeElement;
        const isInput = 
          active?.tagName === 'INPUT' || 
          active?.tagName === 'TEXTAREA' || 
          active?.tagName === 'SELECT' ||
          (active as HTMLElement)?.isContentEditable;
        
        if (!isInput) {
          setIsKeyboardOpen(false);
          setActiveElement(null);
        }
      }, 100);
    };

    // iOS visual viewport resize detection for keyboard
    const handleResize = () => {
      if (isIOS && window.visualViewport) {
        const heightRatio = window.visualViewport.height / window.innerHeight;
        // If visual viewport is significantly smaller, keyboard is likely open
        if (heightRatio < 0.75 && document.activeElement) {
          const active = document.activeElement as HTMLElement;
          const isInput = 
            active.tagName === 'INPUT' || 
            active.tagName === 'TEXTAREA' || 
            active.tagName === 'SELECT' ||
            active.isContentEditable;
          
          if (isInput) {
            setIsKeyboardOpen(true);
            setActiveElement(active);
          }
        }
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    
    if (isIOS && window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      
      if (isIOS && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, [isNative, isIOS]);

  const dismissKeyboard = () => {
    // Blur the active element to dismiss keyboard
    if (activeElement && 'blur' in activeElement) {
      (activeElement as HTMLElement).blur();
    }
    // Also blur document.activeElement as fallback
    if (document.activeElement && 'blur' in document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
    setIsKeyboardOpen(false);
    setActiveElement(null);
  };

  // Don't render on non-native platforms
  if (!isNative) return null;

  // Don't render if keyboard is not open
  if (!isKeyboardOpen) return null;

  return (
    <button
      onClick={dismissKeyboard}
      className="keyboard-dismiss-btn"
      aria-label="Dismiss keyboard"
      type="button"
    >
      <ChevronDown className="w-5 h-5" />
    </button>
  );
}

